import styled from "styled-components";
import { mobileAuthSideContainer, mobileAuthMainContainer } from "../../../styles/authShellMixins";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export const MainContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  background: var(--app-bg-gradient);
  background-size: 400% 400%;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  overflow-x: hidden;

  ${mobileAuthMainContainer}
`;

export const SideContainer = styled.div`
  scrollbar-width: none;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  height: 100vh;
  min-height: 100vh;
  flex: 1 1 auto;
  width: calc(100% - 15vw);
  margin: 0;
  transition: margin-left 0.5s ease-in-out, width 0.5s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  box-sizing: border-box;
  min-width: 0;

  &::-webkit-scrollbar {
    display: none;
  }

  ${mobileAuthSideContainer}
`;

export const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: none;
  padding: 1.25rem 1.25rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin: 0;
  box-sizing: border-box;
`;

export const ButtonContainerLeft = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin: 0.5%;
  gap: 0.5rem;
`;

export const StyledButton = styled.button`
  background: ${props => props.secondary
    ? 'var(--input-bg)'
    : 'var(--accent-secondary-soft)'};
  border: 2px solid ${props => props.secondary
    ? 'var(--border-subtle)'
    : 'var(--border-default)'};
  border-radius: 16px;
  color: ${props => props.secondary
    ? 'var(--text-primary)'
    : 'var(--text-heading)'};
  cursor: pointer;
  padding: 12px 24px;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  min-width: 140px;
  min-height: 44px;
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(135deg,
      ${props => props.secondary
        ? 'var(--input-bg) 0%, var(--surface-glass) 100%'
        : 'var(--accent-secondary-soft) 0%, var(--accent-soft) 100%'}
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 14px;
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 100%;
    border-radius: 14px;
    background: transparent;
    box-shadow: ${props => props.secondary
      ? 'var(--shadow-sm)'
      : '0 6px 15px var(--accent-soft)'};
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: ${props => props.secondary
      ? 'var(--text-muted)'
      : 'var(--accent)'};
    color: var(--text-primary);

    &::before { opacity: 1; }
    &::after  { opacity: 1; }
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media screen and (max-width: 768px) {
    min-width: 120px;
    padding: 10px 20px;
  }
`;

export const HeaderSection = styled.header`
  text-align: center;
  margin-bottom: 1rem;
  position: relative;
`;

export const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 2rem 0;
  text-shadow: 0 4px 20px color-mix(in srgb, var(--accent-secondary) 30%, transparent);
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const FormContainer = styled.div`
  gap: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  border-radius: 20px;
  width: 100%;
  margin: 0;
  position: relative;
  overflow-x: auto;
  box-sizing: border-box;

  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    padding: 0;
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: var(--surface-1);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  font-size: 0.90rem;
  color: var(--text-primary);
  text-align: center;

  thead {
    background: var(--table-header-bg);
  }

  th {
    font-weight: var(--table-header-font-weight);
    color: var(--table-header-color);
    font-size: var(--table-header-font-size);
    text-transform: uppercase;
    letter-spacing: var(--table-header-letter-spacing);
  }

  th, td {
    padding: 12px 16px;
    border-bottom: 1px solid var(--border-subtle);
  }

  tbody tr:hover {
    background-color: var(--accent-soft);
    transition: background 0.2s ease-in-out;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  td[colspan="9"] {
    text-align: center;
    color: var(--text-muted);
    padding: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;

    th, td {
      padding: 8px;
    }
  }

  @media screen and (max-width: 768px) {
    width: 100%;
    font-size: 0.8rem;

    th, td {
      padding: 8px 10px;
    }
  }
`;

export const PaginationContainer = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
`;

export const PageButton = styled.button`
  background: transparent;
  border: 2px solid var(--border-subtle);
  color: var(--text-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);

  &:hover {
    border-color: var(--text-heading);
    color: var(--accent-secondary);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }
`;

export const StyledDatePicker = styled(DatePicker)`
  padding: 8px 10px;
  border: 2px solid var(--border-default);
  border-radius: 16px;
  background: transparent;
  color: var(--text-heading);
  font-size: 14px;
  min-width: 150px;

  &::placeholder {
    color: var(--input-placeholder);
  }

  &:focus {
    outline: none;
    border-color: var(--text-secondary);
    background: var(--table-row-hover);
  }

  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker__header {
    background-color: var(--surface-overlay);
    border-bottom: 1px solid var(--border-default);
  }

  .react-datepicker__day {
    color: var(--text-heading);
  }

  .react-datepicker__day:hover {
    background-color: var(--table-row-hover);
    border-radius: 50%;
  }

  .react-datepicker__day--selected {
    background-color: var(--input-placeholder);
    color: var(--text-on-accent);
    border-radius: 50%;
  }
`;

export const FilterButton = styled.button`
  padding: 8px 16px;
  border-radius: 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.secondary
    ? "var(--input-bg)"
    : "var(--accent-secondary-soft)"};
  border: 2px solid ${props => props.secondary
    ? "var(--border-subtle)"
    : "var(--border-default)"};
  color: ${props => props.secondary
    ? "var(--text-primary)"
    : "var(--text-heading)"};

  &:hover {
    background-color: ${props => props.secondary
      ? "var(--border-subtle)"
      : "var(--border-subtle)"};
    border-color: ${props => props.secondary
      ? "var(--text-muted)"
      : "var(--border-default)"};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ── Inline styles ──────────────────────────────────────────────────────────────

export const loadingWrapperStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100vh",
  gap: "1rem",
  color: "var(--text-heading)",
};

export const loadingSpinnerStyle = {
  width: 40,
  height: 40,
  border: "3px solid var(--border-subtle)",
  borderTop: "3px solid var(--text-heading)",
  borderRadius: "50%",
  animation: "spin 0.8s linear infinite",
};

export const loadingTextStyle = {
  fontSize: "0.95rem",
};

export const badgeBaseStyle = {
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: "999px",
  fontSize: "0.78rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

export const obsCellEmptyStyle = {
  color: "var(--text-muted)",
};

export const filterBarStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  marginBottom: "8px",
  alignItems: "center",
  padding: "12px 16px",
  width: "65%",
};

export const filterLabelStyle = {
  color: "var(--border-strong)",
  fontSize: "0.85rem",
  fontWeight: 500,
};

export const rowsBarStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "8px",
  padding: "8px 16px",
  width: "50%",
  marginLeft: "auto",
  marginBottom: "16px",
};

export const rowsLabelStyle = {
  color: "var(--text-heading)",
  fontSize: "0.85rem",
};

export const rowsSelectStyle = {
  padding: "6px 10px",
  borderRadius: "10px",
  fontSize: "0.85rem",
  background: "var(--table-row-hover)",
  color: "var(--text-on-accent)",
  border: "1px solid var(--border-default)",
  cursor: "pointer",
};

export const resultCountStyle = {
  width: "100%",
  textAlign: "right",
  marginBottom: "6px",
  color: "var(--text-muted)",
  fontSize: "0.8rem",
};

export const thIndexStyle = {
  width: 44,
};

export const tdIndexStyle = {
  color: "var(--text-muted)",
  fontSize: "0.8rem",
  fontWeight: 500,
};

export const tdCodeStyle = {
  color: "var(--info)",
  cursor: "pointer",
  textDecoration: "underline",
  fontWeight: 600,
};

export const tdDateCellStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
};

export const tdDateStyle = {
  fontSize: "0.85rem",
};

export const tdTimeStyle = {
  fontSize: "0.75rem",
  color: "var(--text-muted)",
};

export const tdObsStyle = {
  maxWidth: 180,
  textAlign: "left",
};

export const emptyStateStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  padding: "2.5rem",
  color: "var(--text-muted)",
};

export const emptyStateIconStyle = {
  fontSize: "2rem",
};

export const ellipsisStyle = {
  color: "var(--text-muted)",
  padding: "0 4px",
};

export const pageButtonActiveStyle = {
  borderColor: "var(--text-heading)",
  color: "var(--accent-secondary)",
  background: "var(--accent-secondary-soft)",
};

export const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 240px;
  background: var(--surface-overlay);
  border: 2px solid var(--border-default);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  overflow: hidden;
  z-index: 10;
  box-shadow: var(--shadow-md);
`;

export const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 12px 18px;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--accent-secondary-soft);
  color: var(--text-heading);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--accent-secondary-soft);
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const DesktopOnly = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

export const MobileCards = styled.div`
  display: none;

  @media screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
`;

export const MobileCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  border: 1px solid var(--border-default);
  background: var(--surface-2);
  text-align: left;
  width: 100%;
  box-sizing: border-box;
`;

export const MobileCardTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-heading);
`;

export const MobileCardMeta = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-secondary);
  line-height: 1.4;
  word-break: break-word;
`;

export const MobileCardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.35rem;
`;
